import * as React from "react";
import { connect } from "react-redux";
import { useDispatch, useSelector } from "@teamprevent/admin-redux";

import * as moment from "moment";

import { createValidator } from "not-valid";
import { Button, Divider, Grid, Icon, Label, Loader, Message, Modal } from "semantic-ui-react";

import { Input } from "@neworbit/simpleui-input";

import { TaskModel } from "@teamprevent/common/task/taskActions/models";
import { toast } from "@teamprevent/common/toasts";
import { useValidateStartBeforeEndDate } from "@teamprevent/common/customHooks";

import { ApplicationState } from "../../calendar/models";
import { ApplicationState as BookAppointmentsState, BookAppointmentTaskListModel } from "../../bookAppointments/models";
import { OhCaseState } from "../../crud/ohCase";
import { AppointmentTaskDetails, BookAppointmentForm } from "../appointmentMerging/models";
import { BookAppointmentsApi } from "../bookAppointmentsApi";
import { ClinicDetails } from "../../calendar/clinics/models";
import { clinicApi } from "../../calendar/clinics/clinicApi";

import { AllBookAppointmentTaskTypesList } from "./AllBookAppointmentTaskTypesList ";

import { push } from "redux-little-router";

interface BookMultipleAppointmentsOwnProps {
    onModalClose: () => void;
    loadTasks: () => void;
}

export interface BookMultipleAppointmentsProps {
    bookTask: BookAppointmentTaskListModel;
    clinicId: string;
}

const BookMultipleAppointmentsUnconnected: React.FC<BookMultipleAppointmentsProps & BookMultipleAppointmentsOwnProps> = (props) => {
    const dispatch = useDispatch();

    const clinicId = useSelector((state: ApplicationState) => state.router.params?.clinicId);

    const [clinic, setClinic] = React.useState<ClinicDetails>(null);
    const [bookTasks, setBookTasks] = React.useState<AppointmentTaskDetails[]>([]);
    const [bookAppointmentForm, setBookAppointmentForm] = React.useState<BookAppointmentForm>();

    const [showStartDateErrors, setShowStartDateErrors] = React.useState<boolean>(false);
    const [showEndDateErrors, setShowEndDateErrors] = React.useState<boolean>(false);
    const [showStartBeforeEndDateErrors, setShowStartBeforeEndDateErrors] = React.useState<boolean>(false);

    const [loading, setLoading] = React.useState<boolean>(true);
    const [submitting, setSubmitting] = React.useState<boolean>(false);
    const [bookTasksLoaded, setBookTasksLoaded] = React.useState<boolean>(false);

    const withinClinicTimeValidator = createValidator<moment.Moment>(
        date => date && date.utc(true).isBetween(clinic?.start.utc(true), clinic?.end.utc(true), null, "[]"),
        "Must be within clinic time"
    );

    const useNotInThePastValidator = () =>
        createValidator((value: moment.Moment) => value && value.utc(true).isSameOrAfter(moment().utc(true)), "Must not be in the past");
    const notInPastValidator = useNotInThePastValidator();

    const onStartUpdate = (value: moment.Moment, valid: boolean) => {
        const utcDate = value && value.utc(true);
        const timesValid = useValidateStartBeforeEndDate(utcDate, bookAppointmentForm?.end.utc(true)); // eslint-disable-line react-hooks/rules-of-hooks

        setBookAppointmentForm(prevState => ({
            ...prevState,
            start: utcDate
        }));

        if (!timesValid) {
            setShowStartBeforeEndDateErrors(true);
            return;
        }

        setShowStartBeforeEndDateErrors(false);

        if (valid) {
            setShowStartDateErrors(false);
            return;
        }

        setShowStartDateErrors(true);
    };

    const onEndUpdate = (value: moment.Moment, valid: boolean) => {
        const utcDate = value && value.utc(true);

        const timesValid = useValidateStartBeforeEndDate(bookAppointmentForm?.start.utc(true), utcDate); // eslint-disable-line react-hooks/rules-of-hooks

        setBookAppointmentForm(prevState => ({
            ...prevState,
            end: utcDate
        }));

        if (!timesValid) {
            setShowStartBeforeEndDateErrors(true);
            return;
        }

        setShowStartBeforeEndDateErrors(false);

        if (valid) {
            setShowEndDateErrors(false);
            return;
        }

        setShowEndDateErrors(true);
    };

    const updateBookTaskChildren = (taskModel: TaskModel) => {
        const bookTaskIndex = bookAppointmentForm.bookTaskChildren.map(t => t.id).indexOf(taskModel.id);

        setBookAppointmentForm(prevState => ({
            ...prevState,
            bookTaskChildren: (bookTaskIndex > -1) ? bookAppointmentForm.bookTaskChildren.filter(bt => bt.id !== taskModel.id)
                : [...bookAppointmentForm.bookTaskChildren, taskModel]
        }));
    };

    const onNotesChange = React.useCallback((value: string) => {
        setBookAppointmentForm(prevState => ({
            ...prevState,
            notes: value
        }));
    }, []);

    const submitBookings = async () => {
        setSubmitting(true);
        if (showStartDateErrors || showEndDateErrors || showStartBeforeEndDateErrors) {
            toast.error("Start or End date are invalid");
            setSubmitting(false);
            return;
        }

        try {
            const result = await new BookAppointmentsApi().bookIntoOpenClinic(bookAppointmentForm);
            if (result.success) {
                toast.success("Appointment booked successfully");
                props.loadTasks();

                dispatch(push("/book-appointments"));
            } else {
                toast.error("Appointment booking failed");
            }
        } catch {
            toast.error("Something went wrong");
        }
        setSubmitting(false);
    };

    React.useEffect(() => {
        if (clinicId) {
            (async () => {
                const clinicData = await clinicApi.getClinicDetails(clinicId);

                if (clinicData.start.includes("Z"))
                {
                    setClinic({
                        ...clinicData,
                        start: moment(clinicData.start.slice(0, -9)),
                        end: moment(clinicData.end.slice(0, -9))
                    });
                    return;
                }

                setClinic({
                    ...clinicData,
                    start: moment(clinicData.start),
                    end: moment(clinicData.end)
                });
            })();
        }
    }, [clinicId]);

    React.useEffect(() => {
        if (props.bookTask && clinic && !bookTasksLoaded) {
            getBookAppointments();
        }
    }, [props.bookTask, clinic]);

    const getBookAppointments = () => {
        setLoading(true);
        new BookAppointmentsApi().getBookTasks([props.bookTask.customer.id], props.bookTask.employee.id).then(response => {
            const bookTasksWithin3Months = response.bookingTasks.filter(a => moment(a.task.dueDate).isBefore(moment().add(3, "month")));
            setBookTasks(bookTasksWithin3Months);
            setBookAppointmentForm({
                bookTask: response.bookingTasks.find(t => t.task.id === props.bookTask.id).task,
                bookTaskChildren: [],
                start: clinic?.start,
                end: clinic?.end,
                clinicId: clinic?.id, // eslint-disable-line promise/always-return
                notes: ""
            });

            setBookTasksLoaded(true);
            setLoading(false);
        });
    };

    const onRefresh = () => {
        getBookAppointments();
        setBookAppointmentForm({
            ...bookAppointmentForm,
            bookTaskChildren: [],
        });
    };

    return (
        <>
            <Modal open>
                <Modal.Header content="Appointments" />
                <Modal.Content>
                    <Grid>
                        {loading ? <Loader /> :
                            <>
                                <Grid.Row>
                                    <Grid.Column width={16}>
                                        <Message className="book-tasks-info">Select the book tasks you wish to add to the appointment</Message>
                                    </Grid.Column>
                                </Grid.Row>
                                <AllBookAppointmentTaskTypesList
                                    bookTasks={bookTasks}
                                    selectedBookTask={props.bookTask}
                                    selectTask={updateBookTaskChildren}
                                />

                                <Grid.Row>
                                    <Grid.Column width={16}>
                                        <Divider />
                                    </Grid.Column>
                                </Grid.Row>
                                <Grid.Row>
                                    <Grid.Column width={16}>
                                        <Message.Header>Notes</Message.Header>
                                        <Input.Text
                                            inline
                                            onChange={onNotesChange}
                                            value={bookAppointmentForm.notes}
                                        />
                                    </Grid.Column>
                                </Grid.Row>
                                <Grid.Row>
                                    <Grid.Column width={16}>
                                        <Message>
                                            <Message.Header>Start</Message.Header>
                                            <Input.Datetime
                                                value={bookAppointmentForm?.start}
                                                showErrors={showStartDateErrors}
                                                required
                                                validation={[withinClinicTimeValidator, notInPastValidator]}
                                                onChange={onStartUpdate}
                                            />
                                        </Message>
                                    </Grid.Column>
                                </Grid.Row>
                                <Grid.Row>
                                    <Grid.Column width={16}>
                                        <Message>
                                            <Message.Header>End</Message.Header>
                                            <Input.Datetime
                                                value={bookAppointmentForm?.end}
                                                showErrors={showEndDateErrors}
                                                required
                                                validation={[withinClinicTimeValidator, notInPastValidator]}
                                                onChange={onEndUpdate}
                                            />
                                        </Message>
                                    </Grid.Column>
                                </Grid.Row>
                                {
                                    (showStartBeforeEndDateErrors) &&
                                    <Label basic color="red" pointing content="The start time must be before the end" />
                                }
                            </>
                        }
                    </Grid>
                </Modal.Content>
                <Modal.Actions>
                    <Button icon onClick={onRefresh}>
                        <Icon name='refresh' />
                    </Button>
                    <Button content="Confirm" onClick={submitBookings} disabled={submitting} />
                    <Button content="Cancel" onClick={props.onModalClose} />
                </Modal.Actions>
            </Modal>
        </>
    );
};

const mapStateToProps = (state: ApplicationState & OhCaseState & BookAppointmentsState): BookMultipleAppointmentsProps => {
    return {
        bookTask: state.bookAppointments.bookAppointmentTasks.find(t => t.id === state.router.params.bookTaskId),
        clinicId: state.router.params.clinicId
    };
};

export const BookMultipleAppointments = connect(mapStateToProps)(BookMultipleAppointmentsUnconnected);
